import { t } from '@transifex/native';

import type {
	CreateEventData,
	UpdateEventPayload,
	EventExportPayload,
	CreateEventPayload,
} from 'pkg/actions/events/interface';
import * as flashActions from 'pkg/actions/flashes';
import { show } from 'pkg/actions/flashes';

import * as endpoints from 'pkg/api/endpoints/auto';
import type * as models from 'pkg/api/models';
import { create } from 'pkg/api/models/actions';
import { EventTypes } from 'pkg/api/models/event';
import * as sdk from 'pkg/core/sdk';
import DateTime from 'pkg/datetime';
import * as routes from 'pkg/router/routes';
import { replaceState } from 'pkg/router/state';
import { getTimeZoneByGmtString } from 'pkg/timezone';

import { Event, trackEvent, trackedGroup } from 'components/analytics';

export type {
	CreateEventData,
	UpdateEventPayload,
	EventExportPayload,
	CreateEventPayload,
};

export function parseEventFlags(flags: {
	[key: string]: boolean;
}): models.event.EventFlags[] {
	const parsedFlags: models.event.EventFlags[] = [];

	if (flags && typeof flags === 'object') {
		for (const [key, value] of Object.entries(flags)) {
			if (value) {
				parsedFlags.push(key as models.event.EventFlags);
			}
		}
	}

	return parsedFlags;
}

export const getEventUsers = async (
	organizationId: number,
	eventId: number
) => {
	const request = await sdk.get(
		endpoints.Events.ShowUsers(organizationId, eventId),
		{},
		{}
	);
	const res = await request.json();

	if (request.ok) {
		return res.records;
	}

	return null;
};

export const connectEventSession = async (
	organizationId: number,
	eventId: number,
	sessionId: number
): Promise<boolean> => {
	const request = await sdk.post(
		endpoints.Events.AddTrainingSession(organizationId, eventId, sessionId),
		{},
		{}
	);

	if (request.ok) {
		show({
			title: t('Your session was successfully connected to the event!'),
		});

		trackEvent(Event.ConnectSessionToEvent);

		return true;
	}

	show({
		title: t('Something went wrong'),
		message: t(
			`If you think this error isn't supposed to happen, please contact 360Player support.`
		),
	});

	return false;
};

export async function createEvent(
	organizationId: number,
	data: CreateEventData,
	attachments: models.attachment.Attachment[] = []
): Promise<models.event.Event> {
	const eventData: CreateEventPayload = {
		groupId: data.groupId,
		startsAt: data.startsAt,
		endsAt: data.endsAt,
		title: data.title,
		location: data.location,
		description: data.description,
		type: data.type,
		timezone: data.timezone ? getTimeZoneByGmtString(data.timezone).name : '',
		isPrivate: data.isPrivate,
		isPublic: data.isPublic,
		hideParticipants: data.hideParticipants,
		meetBeforeMinutes: data.meetBeforeMinutes,
		capacity: data.capacity,
		flags: data.flags,
	};

	eventData.startsAtLocal = DateTime.fromTimestamp(
		data.startsAt
	).toDateTimeString();
	eventData.endsAtLocal = DateTime.fromTimestamp(
		data.endsAt
	).toDateTimeString();
	if (
		data.type === EventTypes.Match ||
		data.type === EventTypes.Practice ||
		data.type === EventTypes.Camp
	) {
		eventData.physicalStrainDefault = data.physicalStrainDefault;
	}

	if (data.type === EventTypes.Match && data.game) {
		eventData.game = {
			...data.game,
		};
	}

	if (data.inviteUsers || data.inviteUsersAsPending) {
		eventData.inviteUsersToEvent = true;
		eventData.inviteAdminAndStaffToEvent = true;
		eventData.inviteUsersAsPending = !!data.inviteUsersAsPending;
	}

	if (data.rsvpBefore) {
		eventData.rsvpInterval = data.rsvpInterval;
		eventData.rsvpIntervalCount = data.rsvpIntervalCount;
	}

	if (data.inviteBefore) {
		eventData.inviteInterval = data.inviteInterval;
		eventData.inviteIntervalCount = data.inviteIntervalCount;
	}

	if (data.reminderBefore) {
		eventData.reminderInterval = data.reminderInterval;
		eventData.reminderIntervalCount = data.reminderIntervalCount;
	}

	const [request, response] = await create<
		CreateEventPayload,
		models.event.Event
	>(endpoints.Events.Create(organizationId), eventData);

	if (!request.ok) {
		flashActions.show({
			title: t('Something went wrong'),
			message: t(
				"If you think this error isn't supposed to happen, please contact 360Player support."
			),
		});
		return;
	}

	trackEvent(Event.CalendarEventCreated, {
		...trackedGroup(eventData.groupId),
		event_type: data.type,
		sends_invites: !!data.sendInvites,
	});

	attachments.forEach((a) =>
		sdk.post(endpoints.Events.AddAttachment(organizationId, response.id, a.id))
	);

	const shouldSendInvites = data.sendInvites;

	if (data.selectedUsers.length > 0) {
		await sdk.post(
			endpoints.Events.AddUsers(organizationId, response.id),
			{},
			{
				users: data.selectedUsers.map((u) => u.id),
				sendInvites: shouldSendInvites,
				organizer: data.organizer,
			}
		);
	}

	return response;
}

// Note (liamoberg): Feels like this need a fair bit cleanup, maybe also the documentation in the API
export const updateEvent = async (
	event: models.event.Event,
	organizationId: number,
	data: UpdateEventPayload,
	newAttachments: models.attachment.Attachment[] = [],
	eventUsers: models.eventUser.EventUser[] = []
): Promise<boolean> => {
	if (data.timezone) {
		data.timezone = getTimeZoneByGmtString(data.timezone).name;
	}

	if (data.meetBeforeMinutes) {
		data.meetBeforeMinutes = data.meetBeforeMinutes;
	} else {
		data.meetBeforeMinutes = 0;
	}

	if (data.startsAt) {
		const startsAtDate = DateTime.fromTimestamp(data.startsAt);
		data.startsAtLocal = startsAtDate.toDateTimeString();
	}
	if (data.endsAt) {
		const endsAtDate = DateTime.fromTimestamp(data.endsAt);
		data.endsAtLocal = endsAtDate.toDateTimeString();
	}

	if (data.inviteUsers || data.inviteUsersAsPending) {
		data.inviteUsersToEvent = true;
		data.inviteAdminAndStaffToEvent = true;
		data.inviteUsersAsPending = !!data.inviteUsersAsPending;
	}

	const req = await sdk.patch(
		endpoints.Events.Update(organizationId, event.id),
		{},
		data
	);
	if (!req.ok) {
		const response = await req.json().catch(() => ({}));

		show(
			{
				title: t('Could not update event'),
				message: response?.error,
			},
			req.status
		);

		return false;
	}

	const updatedEvent = await req.json();

	await Promise.all(
		newAttachments.map(
			async (a) =>
				await sdk.post(
					endpoints.Events.AddAttachment(organizationId, updatedEvent.id, a.id)
				)
		)
	);

	const selectedUsers = data.selectedUsers
		?.map((u) => u.id)
		.sort((a, b) => a - b);

	const prevUsers = eventUsers
		.map((eventUser) => eventUser.userId)
		.sort((a, b) => a - b);

	const userDiff = JSON.stringify(selectedUsers) !== JSON.stringify(prevUsers);

	if (userDiff && data.selectedUsers) {
		const selectedUsersIds = data.selectedUsers.map((u) => u.id);

		const removeUsers = eventUsers
			.filter((eventUser) => !selectedUsersIds.includes(eventUser.userId))
			.map((eventUser) => eventUser.userId);

		const addUsers = selectedUsersIds.filter(
			(id) => !eventUsers.map((eventUser) => eventUser.userId).includes(id)
		);

		if (removeUsers.length > 0) {
			await sdk.destroy(
				endpoints.Events.RemoveUsers(organizationId, updatedEvent.id),
				{},
				{
					users: removeUsers,
				}
			);
		}

		if (addUsers.length > 0) {
			const addReq = await sdk.post(
				endpoints.Events.AddUsers(organizationId, updatedEvent.id),
				{},
				{
					users: addUsers,
					sendInvites: data.sendInvites,
					organizer: data.organizer,
				}
			);

			if (!addReq.ok) {
				const response = await addReq.json().catch(() => ({}));

				show(
					{
						title: t('Could not add users'),
						message: response?.error,
					},
					addReq.status
				);
			}
		}
	}

	replaceState(routes.Event.View(organizationId, updatedEvent.id, 'overview'));

	return true;
};

export const addNotes = async (
	organizationId: number,
	eventId: number,
	notes: string
) => {
	const request = await sdk.patch(
		endpoints.Events.Update(organizationId, eventId),
		{},
		{ notes }
	);
	const result = await request.json();

	if (!request.ok) {
		return [false, null];
	}

	return [true, result];
};

export async function deleteEventAttachment(
	organizationId: number,
	eventId: number,
	attachmentId: number
) {
	const request = await sdk.destroy(
		endpoints.Events.RemoveAttachment(organizationId, eventId, attachmentId)
	);
	if (request.ok) {
		return true;
	}

	show({
		title: t('Something went wrong'),
	});

	return false;
}

export async function eventExport(
	organizationId: number,
	payload: EventExportPayload
): Promise<[boolean, string]> {
	const req = await sdk.post(endpoints.Events.Export(organizationId), payload);

	if (req.ok) {
		const text = await req.text();
		return [true, text];
	}

	return [false, ''];
}
